import { useLoaderData } from 'react-router';
import { useState } from 'react';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { Button } from '@rocco/components/button';
import {
    shortenCount,
    formatNumberWithCommas,
    formatTimeSpent,
    formatTimeSpentLong,
} from '@rocco/utils/format-number';
import { ReactComponent as ShareIcon } from '@rocco/icons/svg/share.svg';
import { BookmarkIcon, BookOpenIcon } from 'lucide-react';
import { ReactComponent as EyeIcon } from '@rocco/icons/svg/eye.svg';

import { useFollowSelectorProxy } from '~/modules/moodboards/hooks';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { uiContextAtomSocialSharingActions } from '@rocco/ui/social-sharing/components/SocialSharingActions';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { Flex, useAppDisplayModeContext } from '@archipro-design/aria';
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from '@rocco/components/popover';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import ProjectMobileCTAButtons from '../contacts/ProjectMobileCTAButtons';

// The whole "Views and Engagement" section is hidden until a project reaches
// this many total views, so low-traffic projects don't surface unflattering
// stats (e.g. "0h 14m") before reporting has caught up.
const VIEWS_SECTION_THRESHOLD = 500;

export const ProjectSharesAndTags = () => {
    const { project } = useLoaderData<typeof loader>();
    const { desktop } = useAppDisplayModeContext();
    const [openPopover, setOpenPopover] = useState<string | null>(null);
    const { ItemPageViews, ItemSaves, ItemImpressions, ItemTimeSpent } =
        project.PublicKPI ?? {
            ItemSaves: 0,
            ItemPageViews: 0,
            ItemImpressions: 0,
            ItemTimeSpent: 0,
        };

    const totalViews = (ItemPageViews ?? 0) + (ItemImpressions ?? 0);

    const socialSharingActions = useAtomValue(
        uiContextAtomSocialSharingActions,
    );

    const handleShareClick = () => {
        if (!socialSharingActions) return;
        socialSharingActions.showSocialSharingModal();
    };
    const { isInDesignBoard } = useHandleFollow();
    const { followSelector, openFollowSelector } = useFollowSelectorProxy({
        pinId: project.ID,
        pinType: 'ProjectPin',
    });
    const { showEnquiryCTA, showSpecifierLoginPrompt } = useEnquiryVisibility(
        project.Professional,
    );

    const showEngagementSection = totalViews >= VIEWS_SECTION_THRESHOLD;

    const availableMetrics = [
        {
            name: 'Views',
            value: shortenCount(totalViews ?? 0),
            icon: <EyeIcon />,
            shouldShow: true,
            title: `${formatNumberWithCommas(totalViews)} project impressions.`,
        },
        {
            name: 'Save',
            value:
                ItemSaves && ItemSaves > 100
                    ? shortenCount(ItemSaves ?? 0)
                    : 'Save',
            icon: (
                <BookmarkIcon
                    aria-label={'Save'}
                    className="w-5"
                    fill={
                        !isInDesignBoard(project.ID, 'ProjectPin')
                            ? 'none'
                            : 'currentColor'
                    }
                />
            ),
            title: `${project.Title} has been saved ${formatNumberWithCommas(ItemSaves ?? 0)} times.`,
            shouldShow: ItemSaves && ItemSaves > 100,
            onClick: (e: React.MouseEvent<HTMLElement>) =>
                openFollowSelector(e),
        },
        {
            name: 'Time Spent',
            value: formatTimeSpent(ItemTimeSpent ?? 0),
            icon: <BookOpenIcon className="w-5" />,
            shouldShow: true,
            title: `Together, ArchiPro users spent ${formatTimeSpentLong(ItemTimeSpent ?? 0)} reading this project.`,
        },
        {
            name: 'Share',
            value: 'Share',
            icon: <ShareIcon className="w-5" />,
            onClick: handleShareClick,
            shouldShow: desktop,
            title: `Share ${project.Title}`,
        },
    ];

    return (
        <div className="mx-auto mt-8 mb-4 flex w-full max-w-[113.75rem] flex-col gap-4 pt-3.5 md:mt-0 md:mb-15 md:gap-15">
            <div className="flex flex-col">
                {showEngagementSection && (
                    <>
                        <div className="flex flex-col px-4 md:px-0">
                            <div className="relative flex w-full items-start gap-18 border-t border-[#141414] opacity-20" />
                            <div className="my-4 text-base leading-[130%] font-medium text-[#141414] opacity-50">
                                {`Views and Engagement`}
                            </div>
                        </div>

                        <div className="scrollbar-hide flex w-full flex-row gap-3 overflow-x-auto pl-4 md:pl-0">
                            {availableMetrics.map((metric) => {
                                if (!metric.shouldShow) return null;

                                return (
                                    <Popover
                                        key={metric.name}
                                        open={openPopover === metric.name}
                                        onOpenChange={(open) =>
                                            setOpenPopover(
                                                open ? metric.name : null,
                                            )
                                        }
                                    >
                                        <PopoverTrigger asChild>
                                            <Button
                                                color="secondary"
                                                weight="normal"
                                                className={cn(
                                                    'shrink-0 gap-3',
                                                    !metric?.onClick &&
                                                        'cursor-default',
                                                )}
                                                onClick={(e) =>
                                                    metric?.onClick?.(e)
                                                }
                                                onMouseEnter={() =>
                                                    setOpenPopover(metric.name)
                                                }
                                                onMouseLeave={() =>
                                                    setOpenPopover(null)
                                                }
                                            >
                                                {metric.icon}
                                                {metric.value}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            side="top"
                                            className="max-w-xs border border-gray-200 bg-gray-200 p-2.5 text-sm shadow-lg"
                                        >
                                            {metric.title}
                                        </PopoverContent>
                                    </Popover>
                                );
                            })}
                            <div className="h-1 w-2"></div>
                        </div>
                    </>
                )}
                {!desktop && (
                    <Flex column gap="space-8" className="px-4 py-8">
                        <ProjectMobileCTAButtons
                            project={project}
                            showEnquiryCTA={showEnquiryCTA}
                            showSpecifierLoginPrompt={showSpecifierLoginPrompt}
                            showWebsiteCTA={false}
                        />
                    </Flex>
                )}
            </div>
            {/* Temporarily hiding tags until we can add better implementation */}
            {/* {searchTags.length > 0 && (
                <div className="flex flex-col">
                    <div className="flex flex-col px-4 md:px-0">
                        <div className="relative flex w-full items-start gap-18 border-t border-[#141414] opacity-20" />
                        <div className="my-4 text-base font-medium leading-[130%] text-[#141414] opacity-50">
                            Tags
                        </div>
                    </div>

                    <div className="flex w-full flex-row gap-3 overflow-x-auto pl-4 md:pl-0">
                        {searchTags.map((tag) => {
                            if (!tag) return null;

                            return (
                                <Button
                                    key={tag.ID}
                                    color="secondary"
                                    className="shrink-0 cursor-default gap-3"
                                    weight="normal"
                                >
                                    {capitalizeFirstLetter(tag.Text)}
                                </Button>
                            );
                        })}
                        <div className="h-1 w-2"></div>
                    </div>
                </div>
            )} */}
            {followSelector}
        </div>
    );
};
